// use-models.ts — Models suite, catalog helpers.

"use client";

import { useCallback, useMemo } from "react";
import type { ModelsListResponse } from "@cabal/shared/domain/llm/model/types.js";
import { useApi } from "@/contexts/api";
import { useQueryHook } from "@/lib/query/use-query-hook";
import { QUERY_STATUS, type QueryResult } from "@/types/async-protocol";
import { modelsKeys } from "./keys";

type ProviderWithModels = ModelsListResponse["providers"][number];
type ModelInfo = ProviderWithModels["models"][number];

export interface CabalModel extends ModelInfo {
  vendorSlug: string;
  vendorName: string;
  familyKey: string;
}

export type Model = CabalModel;

export const ModelItemType = {
  Model: "model",
  Family: "family",
} as const;
export type ModelItemType = (typeof ModelItemType)[keyof typeof ModelItemType];

export interface ModelFamilyRowItem {
  displayName: string;
  modelId?: string;
  familyKey?: string;
  itemType: ModelItemType;
}

interface VendorListItem {
  vendorId: string;
  vendorName: string;
}

interface VendorModelInput {
  modelsById: Record<string, CabalModel>;
  vendorToModels: Record<string, string[]>;
}

interface ModelCatalogActionsInput extends VendorModelInput {
  families: Record<string, string[]>;
  sortedVendors: string[];
}

interface ModelCatalogActions {
  getModel: (id: string) => CabalModel | undefined;
  getUniqueVendors: (modelIdentifiers: string[]) => string[];
  getVendors: () => VendorListItem[];
  getFamiliesRow: (vendorId: string) => ModelFamilyRowItem[];
  getModelsInFamily: (familyKey: string) => string[];
}

interface BasicModelCatalogActions {
  getModel: (id: string) => CabalModel | undefined;
  getUniqueVendors: (modelIdentifiers: string[]) => string[];
  getModelsInFamily: (familyKey: string) => string[];
}

interface FamilyRowActionInput extends VendorModelInput {}

interface VendorListActionInput extends VendorModelInput {
  sortedVendors: string[];
}

interface ModelsSnapshotInput extends ModelCatalogActions {
  allModels: CabalModel[];
  modelsResult: QueryResult<ModelsListResponse, Error>;
}

type ModelsSnapshot = QueryResult<CabalModel[], Error> & {
  getModel: (id: string) => CabalModel | undefined;
  getUniqueVendors: (modelIdentifiers: string[]) => string[];
  getVendors: () => VendorListItem[];
  getFamiliesRow: (vendorId: string) => ModelFamilyRowItem[];
  getModelsInFamily: (familyKey: string) => string[];
  isLoading: boolean;
  refreshModels: () => void;
};

export function priceTierToStrength(priceTier: number): 0 | 1 | 2 | 3 {
  if (priceTier <= 0) return 0;
  if (priceTier === 1) return 1;
  if (priceTier === 2) return 2;
  return 3;
}

const EMPTY_PROVIDERS: ProviderWithModels[] = [];
const UNKNOWN_MODEL_DISPLAY_ORDER = Number.POSITIVE_INFINITY;

function getModelDisplayOrder(input: {
  modelsById: Record<string, CabalModel>;
  modelId: string | undefined;
}): number {
  if (!input.modelId) {
    return UNKNOWN_MODEL_DISPLAY_ORDER;
  }

  return input.modelsById[input.modelId]?.displayOrder ?? UNKNOWN_MODEL_DISPLAY_ORDER;
}

function compareModelIdsByDisplayOrder(input: {
  modelsById: Record<string, CabalModel>;
  leftModelId: string | undefined;
  rightModelId: string | undefined;
}): number {
  return (
    getModelDisplayOrder({
      modelsById: input.modelsById,
      modelId: input.leftModelId,
    }) -
    getModelDisplayOrder({
      modelsById: input.modelsById,
      modelId: input.rightModelId,
    })
  );
}

function compareVendorModelIdsByDisplayOrder(input: {
  modelsById: Record<string, CabalModel>;
  leftModelId: string;
  rightModelId: string;
}): number {
  return compareModelIdsByDisplayOrder({
    modelsById: input.modelsById,
    leftModelId: input.leftModelId,
    rightModelId: input.rightModelId,
  });
}

function compareVendorsByFirstModelOrder(input: {
  modelsById: Record<string, CabalModel>;
  vendorToModels: Record<string, string[]>;
  leftVendor: string;
  rightVendor: string;
}): number {
  const orderComparison = compareModelIdsByDisplayOrder({
    modelsById: input.modelsById,
    leftModelId: input.vendorToModels[input.leftVendor]?.[0],
    rightModelId: input.vendorToModels[input.rightVendor]?.[0],
  });

  if (orderComparison !== 0) {
    return orderComparison;
  }

  return input.leftVendor.localeCompare(input.rightVendor);
}

function selectVendorListItem(input: {
  modelsById: Record<string, CabalModel>;
  vendorId: string;
  vendorToModels: Record<string, string[]>;
}): VendorListItem | null {
  const vendorName = selectVendorName(input);
  return vendorName ? { vendorId: input.vendorId, vendorName } : null;
}

function selectVendorName(input: {
  modelsById: Record<string, CabalModel>;
  vendorId: string;
  vendorToModels: Record<string, string[]>;
}): string | undefined {
  const firstModelId = input.vendorToModels[input.vendorId]?.[0];
  return firstModelId ? input.modelsById[firstModelId]?.vendorName : undefined;
}

function getModelFamilyRowSortModelId(input: {
  item: ModelFamilyRowItem;
  familyToModels: Record<string, string[]>;
}): string | undefined {
  if (input.item.itemType === ModelItemType.Model) {
    return input.item.modelId;
  }

  if (!input.item.familyKey) {
    return undefined;
  }

  return input.familyToModels[input.item.familyKey]?.[0];
}

function compareModelFamilyRowItems(input: {
  modelsById: Record<string, CabalModel>;
  familyToModels: Record<string, string[]>;
  leftItem: ModelFamilyRowItem;
  rightItem: ModelFamilyRowItem;
}): number {
  return compareModelIdsByDisplayOrder({
    modelsById: input.modelsById,
    leftModelId: getModelFamilyRowSortModelId({
      item: input.leftItem,
      familyToModels: input.familyToModels,
    }),
    rightModelId: getModelFamilyRowSortModelId({
      item: input.rightItem,
      familyToModels: input.familyToModels,
    }),
  });
}

export function useModels(): ModelsSnapshot {
  const api = useApi();
  const modelsResult = useQueryHook<ModelsListResponse, Error>({
    queryKey: modelsKeys.all,
    queryFn: () => api.models.list(),
  });
  const rawProviders = modelsResult.data?.providers ?? EMPTY_PROVIDERS;
  const activeProviders = useActiveModelProviders(rawProviders);
  const modelsById = useModelsById(activeProviders);
  const families = useModelFamilies(modelsById);
  const vendorToModels = useVendorToModels({ activeProviders, modelsById });
  const sortedVendors = useSortedVendors({ modelsById, vendorToModels });
  const actions = useModelCatalogActions({
    families,
    modelsById,
    sortedVendors,
    vendorToModels,
  });
  const allModels = useMemo<CabalModel[]>(
    () => Object.values(modelsById),
    [modelsById],
  );

  return useModelsSnapshot({
    allModels,
    modelsResult,
    ...actions,
  });
}

function useActiveModelProviders(
  rawProviders: readonly ProviderWithModels[],
): ProviderWithModels[] {
  return useMemo(() => {
    return rawProviders
      .filter((provider) => provider.vendor.isActive)
      .map((provider) => ({
        ...provider,
        models: [...provider.models]
          .filter((model) => model.isActive)
          .sort((left, right) => left.displayOrder - right.displayOrder),
      }))
      .filter((provider) => provider.models.length > 0);
  }, [rawProviders]);
}

function useModelsById(
  activeProviders: readonly ProviderWithModels[],
): Record<string, CabalModel> {
  return useMemo<Record<string, CabalModel>>(() => {
    return activeProviders.reduce<Record<string, CabalModel>>(
      (acc, provider) => {
        provider.models.forEach((model) => {
          const modelId = String(model.id);
          acc[modelId] = {
            ...model,
            vendorSlug: provider.providerKey,
            vendorName: provider.vendor.displayName,
            familyKey: model.modelFamily ?? model.displayName,
          };
        });
        return acc;
      },
      {},
    );
  }, [activeProviders]);
}

function useModelFamilies(
  modelsById: Record<string, CabalModel>,
): Record<string, string[]> {
  return useMemo<Record<string, string[]>>(() => {
    const groupedFamilies = Object.values(modelsById).reduce<
      Record<string, string[]>
    >((acc, model) => {
      const existingModelIds = acc[model.familyKey] ?? [];
      acc[model.familyKey] = [...existingModelIds, String(model.id)];
      return acc;
    }, {});

    return Object.entries(groupedFamilies).reduce<Record<string, string[]>>(
      (acc, [familyKey, modelIds]) => {
        acc[familyKey] = [...modelIds].sort((leftModelId, rightModelId) => {
          return compareModelIdsByDisplayOrder({
            modelsById,
            leftModelId,
            rightModelId,
          });
        });
        return acc;
      },
      {},
    );
  }, [modelsById]);
}

function useVendorToModels({
  activeProviders,
  modelsById,
}: {
  activeProviders: readonly ProviderWithModels[];
  modelsById: Record<string, CabalModel>;
}): Record<string, string[]> {
  return useMemo<Record<string, string[]>>(() => {
    return activeProviders.reduce<Record<string, string[]>>((acc, provider) => {
      acc[provider.providerKey] = provider.models
        .map((model) => String(model.id))
        .sort((leftModelId, rightModelId) =>
          compareVendorModelIdsByDisplayOrder({
            modelsById,
            leftModelId,
            rightModelId,
          }),
        );
      return acc;
    }, {});
  }, [activeProviders, modelsById]);
}

function useSortedVendors({
  modelsById,
  vendorToModels,
}: VendorModelInput): string[] {
  return useMemo(() => {
    return Object.keys(vendorToModels).sort((leftVendor, rightVendor) => {
      return compareVendorsByFirstModelOrder({
        modelsById,
        vendorToModels,
        leftVendor,
        rightVendor,
      });
    });
  }, [vendorToModels, modelsById]);
}

function useModelCatalogActions({
  families,
  modelsById,
  sortedVendors,
  vendorToModels,
}: ModelCatalogActionsInput): ModelCatalogActions {
  const basicActions = useBasicModelCatalogActions({ families, modelsById });
  const getVendors = useVendorListAction({
    modelsById,
    sortedVendors,
    vendorToModels,
  });
  const getFamiliesRow = useFamilyRowAction({ modelsById, vendorToModels });

  return useMemo(
    () => ({
      ...basicActions,
      getFamiliesRow,
      getVendors,
    }),
    [basicActions, getFamiliesRow, getVendors],
  );
}

function useBasicModelCatalogActions({
  families,
  modelsById,
}: {
  families: Record<string, string[]>;
  modelsById: Record<string, CabalModel>;
}): BasicModelCatalogActions {
  const getModel = useCallback(
    (id: string): CabalModel | undefined => {
      return modelsById[id];
    },
    [modelsById],
  );

  const getUniqueVendors = useCallback(
    (modelIdentifiers: string[]): string[] => {
      if (!Array.isArray(modelIdentifiers) || modelIdentifiers.length === 0) {
        return [];
      }

      const uniqueVendors = modelIdentifiers.reduce<Set<string>>(
        (acc, modelId) => {
          const vendorSlug = modelsById[modelId]?.vendorSlug;
          if (vendorSlug) {
            acc.add(vendorSlug);
          }
          return acc;
        },
        new Set<string>(),
      );

      return Array.from(uniqueVendors);
    },
    [modelsById],
  );

  const getModelsInFamily = useCallback(
    (familyKey: string): string[] => {
      return families[familyKey] ?? [];
    },
    [families],
  );

  return useMemo(
    () => ({ getModel, getModelsInFamily, getUniqueVendors }),
    [getModel, getModelsInFamily, getUniqueVendors],
  );
}

function useVendorListAction({
  modelsById,
  sortedVendors,
  vendorToModels,
}: VendorListActionInput): () => VendorListItem[] {
  return useCallback((): VendorListItem[] => {
    return sortedVendors
      .map((vendorId) =>
        selectVendorListItem({ modelsById, vendorId, vendorToModels }),
      )
      .filter((item): item is VendorListItem => item != null);
  }, [modelsById, sortedVendors, vendorToModels]);
}

function selectFamilyToModels(input: {
  modelsById: Record<string, CabalModel>;
  vendorModelIds: readonly string[];
}): Record<string, string[]> {
  return input.vendorModelIds.reduce<Record<string, string[]>>((acc, modelId) => {
    const familyKey = input.modelsById[modelId]?.familyKey;
    if (!familyKey) {
      return acc;
    }

    const existingModelIds = acc[familyKey] ?? [];
    acc[familyKey] = [...existingModelIds, modelId];
    return acc;
  }, {});
}

function selectModelFamilyRowItem(input: {
  familyKey: string;
  modelIds: readonly string[];
  modelsById: Record<string, CabalModel>;
}): ModelFamilyRowItem | null {
  const firstModel = input.modelsById[input.modelIds[0]];
  if (!firstModel) {
    return null;
  }

  return input.modelIds.length === 1
    ? {
        displayName: firstModel.displayName,
        modelId: input.modelIds[0],
        itemType: ModelItemType.Model,
      }
    : {
        displayName: input.familyKey,
        familyKey: input.familyKey,
        itemType: ModelItemType.Family,
      };
}

function selectSortedFamilyRowItems(input: {
  familyToModels: Record<string, string[]>;
  modelsById: Record<string, CabalModel>;
}): ModelFamilyRowItem[] {
  return Object.entries(input.familyToModels)
    .map(([familyKey, modelIds]) =>
      selectModelFamilyRowItem({ familyKey, modelIds, modelsById: input.modelsById }),
    )
    .filter((item): item is ModelFamilyRowItem => item != null)
    .sort((leftItem, rightItem) =>
      compareModelFamilyRowItems({
        modelsById: input.modelsById,
        familyToModels: input.familyToModels,
        leftItem,
        rightItem,
      }),
    );
}

function useFamilyRowAction({
  modelsById,
  vendorToModels,
}: FamilyRowActionInput): (vendorId: string) => ModelFamilyRowItem[] {
  return useCallback(
    (vendorId: string): ModelFamilyRowItem[] => {
      const vendorModelIds = vendorToModels[vendorId] ?? [];
      if (vendorModelIds.length === 0) {
        return [];
      }

      const familyToModels = selectFamilyToModels({
        modelsById,
        vendorModelIds,
      });
      return selectSortedFamilyRowItems({
        familyToModels,
        modelsById,
      });
    },
    [vendorToModels, modelsById],
  );
}

function useModelsSnapshot({
  allModels,
  getFamiliesRow,
  getModel,
  getModelsInFamily,
  getUniqueVendors,
  getVendors,
  modelsResult,
}: ModelsSnapshotInput): ModelsSnapshot {
  const modelsResultData = modelsResult.data;
  const modelsResultError = modelsResult.error;
  const modelsResultRefetch = modelsResult.refetch;
  const modelsResultStatus = modelsResult.status;
  return useMemo<ModelsSnapshot>(
    () => ({
      data: modelsResultData ? allModels : null,
      status: modelsResultStatus,
      error: modelsResultError,
      refetch: modelsResultRefetch,
      getModel,
      getUniqueVendors,
      getVendors,
      getFamiliesRow,
      getModelsInFamily,
      isLoading: modelsResultStatus === QUERY_STATUS.Loading,
      refreshModels: modelsResultRefetch,
    }),
    [
      allModels,
      getFamiliesRow,
      getModel,
      getModelsInFamily,
      getUniqueVendors,
      getVendors,
      modelsResultData,
      modelsResultError,
      modelsResultRefetch,
      modelsResultStatus,
    ],
  );
}
